// Topics: the people, organizations and places the pipeline links from
// story overviews to Wikidata (tasks/entities.py), shown at /topic/<slug>
// and on /topics (2026-10-15).

// Too broad to be a topic anyone looks up: they sit in a large share of all
// stories, so their page is a pile of everything. Kept off search engines
// and out of the /topics lists (still reachable on the site). By Wikidata
// id, so a rename can't let one back in.
export const BROAD_TOPICS = new Set([
  'Q30', // United States
  'Q29468', // Republican Party
  'Q29552', // Democratic Party
  'Q66096', // United States Senate
  'Q11701', // United States House of Representatives
  'Q11268', // United States Congress
  'Q1355327', // Executive Office of the President
  'Q35525', // White House
])

// A topic page with fewer stories than this is thin: noindex, and left out
// of the sitemap.
export const TOPIC_MIN_STORIES = 3

// Underlines in a story's overview (the one place a topic is linked from a
// story) go to what a reader might want to know more about, not to what
// everyone knows (Sam, 2026-10-16: "army" shouldn't be underlined).
//
// Known worldwide: Wikipedia has an article on it in at least this many
// languages (entities.languages). Known to Pano readers: in more than
// FAMILIAR_STORIES Pano stories. Page views don't work for this: they
// measure who is in the news.
//
// An organization is left un-underlined by either (2026-10-16): the FBI 99
// languages, ICE 40, the Army 79, Google 189; AIPAC 39 and Turning Point USA
// 32 stay underlined.
//
// A person only by both (2026-10-25, Sam: a Brazil runoff story underlined
// Flávio Bolsonaro, 34 languages, and not Lula, 128). World fame alone isn't
// what a Pano reader knows (Lula, Netanyahu, Xi are in a few Pano stories
// each), and Pano's count alone isn't either: a race covered daily makes
// its candidates "familiar" in weeks (Abdul El-Sayed 55 stories, 29
// languages; Ken Paxton 43, 20). Both, on 2026-10-25: Trump, Hegseth,
// Zelenskyy, Vance, Graham, Putin, Musk.
export const FAMOUS_LANGUAGES = { organization: 40, person: 50 }
export const FAMILIAR_STORIES = 40
export const UNDERLINE_KINDS = new Set(['person', 'organization'])
export const MAX_UNDERLINES = 4

// The name a topic goes by on Pano: Wikidata's label, minus a leading
// "United States" on organizations (a US news site says "Department of
// Justice"), first letter up (Wikidata labels some items in lowercase:
// "second presidency of Donald Trump").
export function topicName(name, kind) {
  let n = String(name || '')
  if (kind === 'organization') n = n.replace(/^United States (?=\S)/, '')
  return n.charAt(0).toUpperCase() + n.slice(1)
}

// Wikidata's short description without its trailing dates ("American
// politician (born 1963)": the date tells namesakes apart, noise on Pano),
// wherever a topic's description shows (2026-10-25).
const DESCRIPTION_DATES =
  /\s*\((?:born |c\. )?\d{3,4}(?:\s*[–-]\s*\d{0,4})?\)\s*$/
export function topicDescription(description) {
  return String(description || '').replace(DESCRIPTION_DATES, '')
}

// Two letters for a topic without a photo: "Department of Homeland
// Security" is DH, not DO.
export function topicInitials(name) {
  return String(name || '')
    .split(/\s+/)
    .filter((w) => w && !/^(of|the|and|for|de|la|&)$/i.test(w))
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
}

// Where a topic page's "← All topics" goes: the /topics view (tab and
// period, as a query string) the reader left, kept in sessionStorage by
// TopicsChart (2026-10-24).
export const TOPICS_VIEW_KEY = 'pano_topics_view'

// The periods /topics, /storylines and their pages share (2026-10-24).
// Named by their number of days everywhere (Sam, 2026-11-01): "7 days",
// "Last 7 days", "last 7 days", never "this week" or "past month", which
// read as calendar periods and didn't match each other.
export const TOPIC_PERIODS = [
  { id: 'week', label: '7 days', days: 7 },
  { id: 'month', label: '30 days', days: 30 },
  { id: 'quarter', label: '90 days', days: 90 },
]

// The same for a storyline page's "← All storylines" (2026-10-24).
export const STORYLINES_VIEW_KEY = 'pano_storylines_view'
